import { useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, IdCard, BarChart3, Inbox, CreditCard,
  Settings as SettingsIcon, LogOut, Menu, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { Logo } from "@/components/site/Logo";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { Seo } from "@/components/site/Seo";
import { TrialEndedBanner } from "@/components/dashboard/DashBits";

import Overview from "./Overview";
import Companies from "./Companies";
import Profiles from "./Profiles";
import ProfileEditor from "./ProfileEditor";
import Stats from "./Stats";
import ContactRequests from "./ContactRequests";
import Subscriptions from "./Subscriptions";
import Settings from "./Settings";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user.role === "superadmin";

  const nav = [
    { to: "/dashboard", label: t("dash.n_home"), icon: LayoutDashboard, end: true },
    ...(isAdmin ? [{ to: "/dashboard/entreprises", label: t("dash.n_companies"), icon: Building2 }] : []),
    { to: "/dashboard/profils", label: isAdmin ? t("dash.n_profiles") : t("dash.n_myProfiles"), icon: IdCard },
    { to: "/dashboard/statistiques", label: t("dash.n_stats"), icon: BarChart3 },
    { to: "/dashboard/demandes", label: t("dash.n_requests"), icon: Inbox },
    { to: "/dashboard/abonnements", label: isAdmin ? t("dash.n_subs") : t("dash.n_mySub"), icon: CreditCard },
    { to: "/dashboard/parametres", label: t("dash.n_settings"), icon: SettingsIcon },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 border-b border-white/10">
        <Logo size="text-xl" />
        <p className="mt-1 text-[11px] font-mono uppercase tracking-wider text-cyan-400/80">
          {isAdmin ? t("dash.admin") : t("dash.clientSpace")}
        </p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`dash-nav-${n.to.split("/").pop() || "home"}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-colors duration-300 ${isActive ? "bg-brand-gradient text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/10">
        <div className="px-1 mb-2"><LanguageSelector align="left" /></div>
        <div className="px-3.5 py-2 mb-2">
          <p className="text-sm text-white font-medium truncate">{user.name}</p>
          <p className="text-xs text-zinc-500 truncate">{user.email}</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-colors duration-300">
          <ExternalLink className="w-4 h-4" /> {t("dash.viewSite")}
        </a>
        <button onClick={() => { logout(); navigate("/connexion"); }} data-testid="dash-logout"
          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-colors duration-300">
          <LogOut className="w-4 h-4" /> {t("dash.logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#05050A] flex">
      <Seo noindex title="Espace BBR.GO" path="/dashboard" />
      <aside className="hidden lg:block w-64 shrink-0 border-r border-white/10 bg-[#08080f] fixed inset-y-0">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#08080f] border-r border-white/10"><SidebarContent /></div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <div className="lg:hidden sticky top-0 z-40 glass-strong flex items-center justify-between px-4 h-14">
          <button onClick={() => setOpen(true)} data-testid="dash-mobile-menu"><Menu className="w-6 h-6 text-white" /></button>
          <Logo size="text-lg" />
          <LanguageSelector />
        </div>

        <main className="p-5 sm:p-8 max-w-7xl">
          {!isAdmin && <TrialEndedBanner />}
          <Routes>
            <Route index element={<Overview />} />
            {isAdmin && <Route path="entreprises" element={<Companies />} />}
            <Route path="profils" element={<Profiles />} />
            <Route path="profils/:id" element={<ProfileEditor />} />
            <Route path="statistiques" element={<Stats />} />
            <Route path="demandes" element={<ContactRequests />} />
            <Route path="abonnements" element={<Subscriptions />} />
            <Route path="parametres" element={<Settings />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
